import { useMutation } from '@apollo/client';
import type {
  UpdateWizardMutation,
  UpdateWizardMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateWizardDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateWizardTRPC } from './useUpdateWizardTRPC';

export const useUpdateWizard = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateWizardGraphQL, graphqlState] = useMutation(
    UpdateWizardDocument,
    {
      update: (cache) => {
        evictField(cache, 'wizard');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateWizardTRPC();

  const updateWizard = async (
    variables: UpdateWizardMutationVariables
  ): Promise<UpdateWizardMutation> => {
    if (trpcEnabled) {
      const obj = variables.object;

      return trpcMutation.updateWizard({
        RiskId: obj?.RiskId ?? '',
        CurrentStep: obj?.CurrentStep ?? 0,
        Status: obj?.Status,
      });
    }

    const result = await updateWizardGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update wizard');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateWizard,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateWizard,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
